import React, { useState } from 'react';
import { Send, CheckCircle, ExternalLink } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { trackEvent } from '../lib/analytics';

const CALENDAR_URL =
  'https://calendar.google.com/calendar/appointments/schedules/AcZssZ1wxj49gCb_Y2n00J7kDF3HzV1R1ouSPCaaNWOHKCuzmD6PoFq8mpqRgEY3SIwhOwAXa1Nk0DmB';

const Contact = ({ data, variant = 'both' }) => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          name: formData.name,
          title: formData.message,
          email: formData.email,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      trackEvent('contact_form_submit');
      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      console.error('Failed to send email:', err);
      setError('Failed to send message. Please try again or email directly.');
    } finally {
      setSending(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const calendarBlock = (
    <div className="v2-contact-col v2-contact-col--calendar">
      <div className="v2-contact-head">
        <h2 className="section-title">Let's connect</h2>
        <a
          href={CALENDAR_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="calendar-link"
          title="Open full calendar in new tab"
        >
          <span>Open full</span>
          <ExternalLink size={13} />
        </a>
      </div>

      <div className="v2-calendar-card">
        <div className="v2-calendar-embed">
          <iframe
            src={`${CALENDAR_URL}?gv=true`}
            title="Google Calendar booking"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );

  const formBlock = (
    <div className="v2-contact-col v2-contact-col--form">
      <div className="v2-contact-head">
        <h2 className="section-title">Let's build something</h2>
      </div>

      <div className="v2-form-card">
        <p className="v2-form-intro">
          Drop a note and I'll get back within a couple of working days.
        </p>

        {submitted ? (
          <div className="v2-contact-success">
            <CheckCircle size={36} />
            <h3>Message sent</h3>
            <p>Thank you — I'll be in touch soon.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="v2-contact-form">
            {error && <div className="v2-contact-error">{error}</div>}

            <div className="v2-form-row">
              <div className="form-group">
                <label htmlFor="name" className="form-label">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Your name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="message" className="form-label">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="form-textarea"
                placeholder="What would you like to discuss?"
                rows={5}
                required
              />
            </div>

            <button
              type="submit"
              className="btn-primary v2-contact-submit"
              disabled={sending}
            >
              <span>{sending ? 'Sending…' : 'Send message'}</span>
              <Send size={16} />
            </button>
          </form>
        )}

        <div className="v2-contact-direct">
          Prefer email? <a href={`mailto:${data.email}`}>{data.email}</a>
        </div>
      </div>
    </div>
  );

  if (variant === 'calendar') {
    return (
      <section id="schedule" className="section-container v2-schedule-section">
        <div className="content-wrapper v2-schedule-wrap">{calendarBlock}</div>
      </section>
    );
  }

  if (variant === 'form') {
    return (
      <div id="contact" className="v2-contact-form-only">{formBlock}</div>
    );
  }

  return (
    <section id="contact" className="section-container v2-contact-section">
      <div className="content-wrapper">
        <div className="v2-contact-grid">
          {calendarBlock}
          {formBlock}
        </div>
      </div>
    </section>
  );
};

export default Contact;
